import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ProductLine } from 'apis/restaurant-api/merchant-admin/venue/types';
import { RFFFieldErrorWrapper } from 'common/components';
import InputNumber from 'common/components/InputNumber';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { ZERO_DECIMAL_CURRENCIES } from 'common/utils/currencyDecimals';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { ItemDiscountFormValues, ItemDiscountType } from 'modules/campaigns/types';

import { countryConfig } from './countryConfig';
import CampaignFormItem from '../../CampaignFormItem';
import { DiscountedItemCountInput } from '../../discounted-item-count-section/DiscountedItemCountInput';
import { useItemDiscountTypes } from '../useItemDiscountTypes';

type ItemDiscountTypeFormItemProps = {
  country: Country;
  minPercentage?: number;
  maxPercentage?: number;
  disabled: boolean;
  productLines: ProductLine[];
  isEndOfDayCampaign?: boolean;
};

const StyledRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-block-start: 8px;
`;

const StyledDiscountedType = styled.label<{ checked?: boolean; disabled?: boolean }>`
  display: flex;
  padding: 12px;
  align-items: center;
  gap: 12px;
  flex: 1 0 0;
  border: 1px solid;
  border-radius: 8px;
  border-color: ${({ checked, disabled }) => (checked && !disabled ? colors.wolt100 : cbSemanticCssVars.borderPrimary)};
  background-color: ${({ checked, disabled }) => (checked && !disabled ? colors.wolt8 : cbSemanticCssVars.surfaceMain)};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: pointer;

  ${({ disabled }) => disabled && 'pointer-events: none;'}

  &:hover {
    background-color: ${({ checked }) =>
      checked ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const StyledDiscountTypeIconContainer = styled.div<{ checked?: boolean; disabled?: boolean }>`
  display: flex;
  width: 40px;
  height: 40px;
  border-radius: 40px;
  background-color: ${({ checked, disabled }) =>
    checked && !disabled ? colors.wolt100 : cbSemanticCssVars.textTertiary};
  align-items: center;
  justify-content: center;

  h4 {
    color: ${cbSemanticCssVars.surfaceMain};
  }

  svg {
    width: 24px;
    height: 24px;
    fill: ${cbSemanticCssVars.surfaceMain};
    color: ${cbSemanticCssVars.surfaceMain};
  }
`;

const StyledDiscountTypeLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const StyledDiscountTypeTitle = styled(Typography.Body3)`
  align-self: stretch;
`;

const StyledDiscountTypeDescription = styled(Typography.Small)`
  align-self: stretch;
`;

const StyledDiscountedTypeAdditionalInput = styled.div`
  margin-block-start: -8px;
`;

const StyledInputNumber = styled(InputNumber)`
  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    width: 175px;
  }
`;

const StyledInputContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  align-items: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ValidationMessage = styled(Typography.Body3)`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-inline-start: 8px;
  }
`;

export const ItemDiscountTypeFormItem = ({
  minPercentage,
  maxPercentage,
  disabled,
  country,
  productLines,
  isEndOfDayCampaign,
}: ItemDiscountTypeFormItemProps) => {
  const { currency } = useCampaignModuleContext();
  const { formatMessage } = useIntl();
  const { values: formValues } = useFormState<ItemDiscountFormValues>();
  const hasSelectedDiscountItem = !!formValues.itemIds.length || !!formValues.categoryIds.length;
  const itemDiscountTypes = useItemDiscountTypes(isEndOfDayCampaign);

  const allowedItemDiscountTypes = useMemo(() => {
    return itemDiscountTypes.filter(({ type }) => {
      if (isEndOfDayCampaign) {
        return type !== ItemDiscountType.X_FOR_FREE;
      }
      if (countryConfig[country].isAllDiscountTypeEnabled) {
        return true;
      }
      const allowedProductLines = countryConfig[country].config?.[type] || [];
      const intersection = allowedProductLines.filter(element => productLines.includes(element));
      return intersection.length === productLines.length;
    });
  }, [itemDiscountTypes, isEndOfDayCampaign, country, productLines]);

  const currencyDecimals = ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2;

  return (
    <CampaignFormItem
      title={<FormattedMessage id="campaigns.item-discount-type.select-discount" />}
      value={
        <StyledRadioGroup>
          {allowedItemDiscountTypes.map(itemDiscountType => (
            <Field<ItemDiscountFormValues['discountType']>
              name="discountType"
              type="radio"
              value={itemDiscountType.type}
              key={itemDiscountType.type}
            >
              {({ input }) => (
                <>
                  <StyledDiscountedType
                    checked={input.checked}
                    onClick={() => {
                      !disabled && input.onChange(itemDiscountType.type);
                    }}
                    disabled={disabled || !hasSelectedDiscountItem}
                  >
                    <StyledDiscountTypeIconContainer checked={input.checked} disabled={!hasSelectedDiscountItem}>
                      {itemDiscountType.icon}
                    </StyledDiscountTypeIconContainer>
                    <StyledDiscountTypeLabelContainer>
                      <StyledDiscountTypeTitle>{itemDiscountType.title}</StyledDiscountTypeTitle>
                      <StyledDiscountTypeDescription>{itemDiscountType.description}</StyledDiscountTypeDescription>
                    </StyledDiscountTypeLabelContainer>
                  </StyledDiscountedType>
                  {input.checked && hasSelectedDiscountItem && (
                    <StyledDiscountedTypeAdditionalInput>
                      {itemDiscountType.type === 'x_for_free' && (
                        <DiscountedItemCountInput
                          name="freeItemsCount"
                          defaultValue={1}
                          disabled={disabled}
                          messageId="campaigns.form.item-count.item-free"
                          enabledCounts={
                            formValues.itemTriggerCount
                              ? [...Array.from({ length: Number(formValues.itemTriggerCount) }, (_, i) => i + 1)]
                              : undefined
                          }
                        />
                      )}
                      {itemDiscountType.type === 'percentage_off' && (
                        <Field<ItemDiscountFormValues['percentage']> name="percentage" type="number">
                          {field => (
                            <RFFFieldErrorWrapper field={field}>
                              {input => {
                                return (
                                  <StyledInputContainer>
                                    <StyledInputNumber
                                      {...input}
                                      maxDecimals={2}
                                      suffixText={formatMessage({
                                        id: 'campaigns.item-discount-type.percentage-off',
                                      })}
                                      disabled={disabled}
                                      // by default, the number input will increment/decrement by 1 when you scroll the mouse wheel inside it
                                      // this is not the desired behavior for this input, so we prevent it here
                                      onWheel={e =>
                                        'blur' in e.target && typeof e.target.blur === 'function' && e.target.blur()
                                      }
                                    />

                                    <ValidationMessage>
                                      <FormattedMessage
                                        id="campaigns.item-discount-type.percentage-validation"
                                        values={{
                                          min: minPercentage,
                                          max: maxPercentage,
                                        }}
                                      />
                                    </ValidationMessage>
                                  </StyledInputContainer>
                                );
                              }}
                            </RFFFieldErrorWrapper>
                          )}
                        </Field>
                      )}
                      {itemDiscountType.type === 'amount_off' && (
                        <Field<ItemDiscountFormValues['amount']> name="amount" type="number">
                          {field => (
                            <RFFFieldErrorWrapper field={field}>
                              {input => {
                                return (
                                  <StyledInputContainer>
                                    <StyledInputNumber
                                      {...input}
                                      maxDecimals={currencyDecimals}
                                      suffixText={formatMessage(
                                        { id: 'campaigns.item-discount-type.currency-off' },
                                        { currency },
                                      )}
                                      disabled={disabled}
                                      // by default, the number input will increment/decrement by 1 when you scroll the mouse wheel inside it
                                      // this is not the desired behavior for this input, so we prevent it here
                                      onWheel={e =>
                                        'blur' in e.target && typeof e.target.blur === 'function' && e.target.blur()
                                      }
                                    />

                                    <ValidationMessage>
                                      <FormattedMessage id="campaigns.item-discount-type.amount-validation" />
                                    </ValidationMessage>
                                  </StyledInputContainer>
                                );
                              }}
                            </RFFFieldErrorWrapper>
                          )}
                        </Field>
                      )}
                    </StyledDiscountedTypeAdditionalInput>
                  )}
                </>
              )}
            </Field>
          ))}
        </StyledRadioGroup>
      }
    />
  );
};
